import { useState, useEffect } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { globalSphereCache, type SphereInfo, type CachedSphereData } from '@center-platform/spheres/cache';

interface UseUserDataResult {
  info: SphereInfo | null;
  imageUrl?: string;
  thumbnailUrl?: string;
  loading: boolean;
  error?: string;
}

/**
 * Hook to get user data (sphere info + images) with global caching
 * 
 * @param userId - The sphere ID of the user
 * @returns User data with loading state
 */
export function useUserData(userId?: string): UseUserDataResult {
  const apiClients = useApiClientsOrNull();
  const [data, setData] = useState<UseUserDataResult>({
    info: null,
    loading: false
  });

  useEffect(() => {
    if (!userId || !apiClients) {
      setData({ info: null, loading: false });
      return;
    }

    const { graphClient } = apiClients;

    // Check cache first for immediate response
    const cached = globalSphereCache.getCachedSphere(userId);
    if (cached) {
      setData({
        info: cached.info,
        imageUrl: cached.imageUrl,
        thumbnailUrl: cached.thumbnailUrl,
        loading: false
      });
      return;
    }

    // Load from cache/API
    setData(prev => ({ ...prev, loading: true }));

    globalSphereCache.getSphereData(userId, graphClient)
      .then((cached: CachedSphereData) => {
        setData({
          info: cached.info,
          imageUrl: cached.imageUrl,
          thumbnailUrl: cached.thumbnailUrl,
          loading: false
        });
      })
      .catch((error: Error) => {
        setData({
          info: null,
          loading: false,
          error: error.message
        });
      });

  }, [userId, apiClients]);

  return data;
}

/**
 * Hook to get multiple users data with preloading
 * 
 * @param userIds - Array of sphere IDs
 * @returns Map of userId to user data
 */
export function useMultipleUserData(userIds: string[]): { [userId: string]: UseUserDataResult } {
  const apiClients = useApiClientsOrNull();
  const [data, setData] = useState<{ [userId: string]: UseUserDataResult }>({});

  useEffect(() => {
    if (userIds.length === 0 || !apiClients) {
      setData({});
      return;
    }

    const { graphClient } = apiClients;

    // Initialize loading states
    const initialData: { [userId: string]: UseUserDataResult } = {};
    userIds.forEach(userId => {
      const cached = globalSphereCache.getCachedSphere(userId);
      if (cached) {
        initialData[userId] = {
          info: cached.info,
          imageUrl: cached.imageUrl,
          thumbnailUrl: cached.thumbnailUrl,
          loading: false
        };
      } else {
        initialData[userId] = {
          info: null,
          loading: true
        };
      }
    });

    setData(initialData);

    // Preload all users
    globalSphereCache.preloadSpheres(userIds, graphClient)
      .then(() => {
        // Update with loaded data
        const updatedData: { [userId: string]: UseUserDataResult } = {};
        userIds.forEach(userId => {
          const cached = globalSphereCache.getCachedSphere(userId);
          if (cached) {
            updatedData[userId] = {
              info: cached.info,
              imageUrl: cached.imageUrl,
              thumbnailUrl: cached.thumbnailUrl,
              loading: false
            };
          } else {
            updatedData[userId] = {
              info: null,
              loading: false,
              error: 'Failed to load user data'
            };
          }
        });
        setData(updatedData);
      })
      .catch((error: Error) => {
        console.error('Failed to preload users:', error);
        // Set error state for all users
        const errorData: { [userId: string]: UseUserDataResult } = {};
        userIds.forEach(userId => {
          errorData[userId] = {
            info: null,
            loading: false,
            error: error.message
          };
        });
        setData(errorData);
      });

  }, [userIds.join(','), apiClients]); // Use join to properly detect array changes

  return data;
}